/**
 * Minimal, consent-scoped campaign context for an immediate CSP reload.
 * Never replays tracking calls or events. A loaded SDK is not proof of consent.
 */
import { pmError } from './Logger.js';

const SERVICES = { google_analytics: 'analytics', matomo: 'analytics', google_ads: 'marketing', facebook_pixel: 'marketing' };
const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];
const CLICK_KEYS = { google_ads: 'gclid', facebook_pixel: 'fbclid' };
const MAX_AGE_MS = 60_000;
const SCHEMA = 2;

export class TrackingDataPreserver {
    constructor({ activeServices = {}, consentVersion = '', getConsent = () => null } = {}) {
        this.storageKey = 'pixelmate_tracking_data';
        this.activeServices = activeServices;
        this.consentVersion = consentVersion;
        this.getConsent = getConsent;
        this.context = {};
        this.publishedToGtm = false;
        this.restored = false;
    }

    allowedServices(decision = this.getConsent()) {
        if (!this.consentVersion || decision?.version !== this.consentVersion
            || !decision.categories || !['analytics', 'marketing', 'external']
                .every(key => typeof decision.categories[key] === 'boolean')) return [];
        const policy = window.pixelmateGTMConfig;
        const managed = policy && window.pixelmateGTMConsent?.resolve(policy, decision);
        return Object.keys(SERVICES).filter(service => {
            const definition = this.activeServices[service];
            if (definition?.enabled !== true) return false;
            // Ownership must be known even with a disabled/misconfigured container.
            if (definition.viaGtm === true || Object.hasOwn(policy?.services || {}, service)) {
                return managed?.services[service] === true; // Never Advanced pre-consent gates.
            }
            const explicit = decision.services?.[service];
            if (typeof explicit === 'boolean') return explicit;
            const category = definition.category || SERVICES[service];
            return category !== 'necessary' && decision.categories[category] === true;
        });
    }

    static campaignValue(value) {
        return typeof value === 'string' && value.length > 0 && value.length <= 200
            && !/[\u0000-\u001f\u007f]/.test(value) ? value : null;
    }

    static referrerOrigin(value) {
        if (typeof value !== 'string' || !value || value.length > 4096) return null;
        try {
            const url = new URL(value);
            return ['https:', 'http:'].includes(url.protocol) ? url.origin : null;
        } catch { return null; }
    }

    static sanitize(service, input) {
        const result = {};
        if (!input || typeof input !== 'object' || Array.isArray(input)) return result;
        CAMPAIGN_KEYS.forEach(key => {
            const value = TrackingDataPreserver.campaignValue(input[key]);
            if (value !== null) result[key] = value;
        });
        const referrer = TrackingDataPreserver.referrerOrigin(input.referrer);
        if (referrer) result.referrer = referrer;
        const key = CLICK_KEYS[service];
        if (key && typeof input[key] === 'string' && /^[A-Za-z0-9._~-]{1,256}$/.test(input[key])) result[key] = input[key];
        return result;
    }

    clearStoredData() {
        try { sessionStorage.removeItem(this.storageKey); } catch {
            // Storage unavailable: never fall back to cookies or another store.
        }
    }

    saveTrackingDataBeforeReload() {
        this.clearStoredData(); // Also removes legacy snapshots on rejection.
        try {
            const allowed = this.allowedServices();
            if (!allowed.length) return false;
            const params = new URLSearchParams(window.location.search);
            const input = { referrer: document.referrer };
            [...CAMPAIGN_KEYS, ...Object.values(CLICK_KEYS)].forEach(key => { input[key] = params.get(key); });
            const services = {};
            allowed.forEach(service => {
                const payload = TrackingDataPreserver.sanitize(service, input);
                if (Object.keys(payload).length) services[service] = payload;
            });
            if (!Object.keys(services).length) return false;
            sessionStorage.setItem(this.storageKey, JSON.stringify({
                schema: SCHEMA, consentVersion: this.consentVersion, timestamp: Date.now(), services
            }));
            return true;
        } catch {
            // Do not log IDs, URLs or storage exception payloads.
            pmError('Campaign context could not be saved');
            return false;
        }
    }

    restoreTrackingData() {
        if (this.restored) return;
        this.restored = true;
        try {
            const raw = sessionStorage.getItem(this.storageKey);
            // Consume before parsing/delivery: invalid data cannot be replayed.
            sessionStorage.removeItem(this.storageKey);
            if (!raw) return;
            const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
            if (navigation?.type !== 'reload') return;
            const saved = JSON.parse(raw);
            if (saved?.schema !== SCHEMA || saved.consentVersion !== this.consentVersion
                || !Number.isFinite(saved.timestamp) || saved.timestamp > Date.now()
                || Date.now() - saved.timestamp > MAX_AGE_MS
                || !saved.services || typeof saved.services !== 'object' || Array.isArray(saved.services)) return;
            const context = {};
            this.allowedServices().forEach(service => {
                const payload = TrackingDataPreserver.sanitize(service, saved.services[service]);
                if (Object.keys(payload).length) context[service] = payload;
            });
            this.publish(context);
        } catch {
            pmError('Campaign context could not be restored');
        }
    }

    onConsentChange(decision) {
        this.clearStoredData();
        const context = {};
        this.allowedServices(decision).forEach(service => {
            if (this.context[service]) context[service] = this.context[service];
        });
        this.publish(context);
    }

    publish(context) {
        this.context = Object.fromEntries(Object.entries(context).map(([key, value]) => [key, Object.freeze({ ...value })]));
        window.pixelmateCampaignData = Object.freeze({ ...this.context });
        const managed = window.pixelmateGTMConfig?.services || {};
        const gtmContext = Object.fromEntries(Object.entries(this.context).filter(([service]) => Object.hasOwn(managed, service)));
        if (this.publishedToGtm || Object.keys(gtmContext).length) {
            window.dataLayer = window.dataLayer || [];
            // GTM merges nested objects: clear revoked/stale fields first.
            window.dataLayer.push({ pixelmate_campaign: null });
            if (Object.keys(gtmContext).length) window.dataLayer.push({ pixelmate_campaign: gtmContext });
            this.publishedToGtm = true;
        }
        // Deliberately no event property, gtag, fbq, _paq or polling callbacks.
    }
}
